import ViewProductUtils from '@/views/product/utils';
import { PriceType } from '@/constants';
import VQBGateway from '@/gateway/VQB';
import { fragrancesAsLabelValue } from '@/utils/fragrance-map';
import { goalsAsLabelValue } from '@/utils/goals-map';

export default class ConfigurationUtils {
  static urlPrams;
  static get CustomConfigurations() {
    // Fragrance available options can be different between hair, skin and body
    return {
      fragrance: {
        title: 'fragrance',
        options: {
          hair: [...fragrancesAsLabelValue],
          body: [...fragrancesAsLabelValue],
          skin: [
            {
              label: 'Fragrance Free',
              value: 'fragrance-free'
            },
            {
              label: 'Dew You',
              value: 'dew you'
            }
          ]
        },
        enable: true,
        max: 1
      },
      // Fragrance Strength available options
      fragrance_strength: {
        title: 'fragrance strength',
        options: [
          {
            label: 'light',
            value: 'light'
          },
          {
            label: 'medium',
            value: 'medium'
          },
          {
            label: 'strong',
            value: 'strong'
          }
        ],
        enable: true,
        max: 1
      },
      // Hair Goals available options for hair quiz results only
      hair_goals: {
        title: 'hair goals',
        options: goalsAsLabelValue,
        enable: false,
        max: 5
      },
      // Styling Goals available options for hair profile only
      style_goals: {
        title: 'styling goals',
        options: [
          {
            label: 'Anti-pollution',
            value: 'anti-pollution'
          },
          {
            label: 'Curl definition',
            value: 'curl definition'
          },
          {
            label: 'Deep condition',
            value: 'deep condition & repair'
          },
          {
            label: 'Tame frizz',
            value: 'frizz control'
          },
          {
            label: 'Humidity shield',
            value: 'humidity shield'
          },
          {
            label: 'Smooth',
            value: 'smoothing'
          },
          {
            label: 'Volumize',
            value: 'volumize'
          }
        ],
        enable: false,
        max: 2
      },
      // Skin Goals available options for skin profile only
      skin_goals: {
        title: 'skin goals',
        options: [
          {
            label: 'hydrate',
            value: 'hydrate'
          },
          {
            label: 'brighten',
            value: 'brighten'
          },
          {
            label: 'awaken',
            value: 'awaken'
          },
          {
            label: 'calm/soothe',
            value: 'calm/soothe'
          },
          {
            label: 'even tone',
            value: 'even tone'
          },
          {
            label: 'reduce breakouts',
            value: 'reduce breakouts'
          },
          {
            label: 'minimize redness',
            value: 'minimize redness'
          },
          {
            label: 'minimize pores',
            value: 'minimize pores'
          },
          {
            label: 'tighten',
            value: 'tighten'
          },
          {
            label: 'reduce shine',
            value: 'reduce shine'
          },
          {
            label: 'smooth',
            value: 'smooth'
          },
          {
            label: 'anti-pollution',
            value: 'anti-pollution'
          }
        ],
        enable: false,
        max: 3
      },
      allowed_properties: [
        '__fragrance',
        '__fragrance_strength',
        '__hair_goals',
        '__hair_type',
        '__hair_structure',
        '__style_goals',
        '__scalp_moisture',
        '__silicone_free',
        '__hold_strength',
        '__skin_goals',
        '__skin_type',
        '__skin_sensitivity',
        '__skin_makeup',
        '__body_moisture',
        '__age',
        '__hair_color',
        '__color_treatment',
        '__color_treatment_frequency',
        '__styling',
        '__styling_frequency',
        '__anti_brassiness',
        '__damage_level',
        '__mechanical_damage_level',
        '__chemical_damage_level',
        '__heat_damage_level',
        '__profile_name',
        '__profile_id',
        '__profile_type',
        '__product_handle'
      ],
      default_recommended_handles: {
        hair: [
          'sos-shampoo',
          'sos-conditioner',
          'sos-leave-in-treatment',
          'sos-hair-mask',
          'shampoo',
          'conditioner',
          'hair-mask',
          'styling-primer',
          'hair-serum',
          'leave-in-conditioner',
          'cowash',
          'comb',
          'travel-toiletry-set',
          'purple-shampoo',
          'purple-conditioner',
          'purple-hair-mask'
        ],
        skin: [],
        // skin: [
        //     "face-serum",
        //     "face-cleanser-jelly",
        //     "face-cleanser-micellar",
        //     "face-moisturizer-cream",
        //     "face-moisturizer-gel",
        //     "face-moisturizer-lotion"
        // ],
        body: ['body-lotion', 'body-wash']
      }
    };
  }

  static createUrlPramsInstance() {
    return new URLSearchParams(window.location.search);
  }

  static getUrlPrams() {
    if (!ConfigurationUtils.urlPrams)
      ConfigurationUtils.urlPrams = ConfigurationUtils.createUrlPramsInstance();

    return ConfigurationUtils.urlPrams;
  }

  static getUrlPramByKey(key) {
    return ConfigurationUtils.getUrlPrams()?.get(key);
  }

  static setUrlParam({ key, value }) {
    ConfigurationUtils.getUrlPrams().set(key, value);
    window.history.replaceState(
      {},
      '',
      `${
        window.location.pathname
      }?${ConfigurationUtils.getUrlPrams().toString()}`
    );
  }

  /**
   *
   * @param {Object} settings
   * @param {number} originalPrice
   * @param {string} productName
   * @param {string} productHandle
   * @param {string | undefined} priceType
   * @returns {Promise<{originalPrice}|{discountedPrice: number, originalPrice}>}
   */
  static async getPrice({
    settings,
    originalPrice,
    productName,
    productHandle,
    priceType,
    enableWhenEditing
  }) {
    const specificDiscountValue = settings?.specific_discount_value;
    const enableSpecificDiscount = settings?.enable_specific_discount;
    const enableByType = !priceType
      ? true
      : (priceType === PriceType.ONE_TIME &&
          settings?.enable_specific_discount_one_time) ||
        (priceType === PriceType.SUBSCRIPTION &&
          settings?.enable_specific_discount_subscriptions);

    const isSpecificDiscount = await ViewProductUtils.isProductSpecificDiscount(
      {
        settings,
        product: {
          title: productName,
          handle: productHandle
        }
      }
    );

    const isEnabled =
      enableSpecificDiscount &&
      isSpecificDiscount &&
      enableByType &&
      enableWhenEditing;

    if (!isEnabled)
      return {
        originalPrice
      };

    const discountRatio = (
      (originalPrice * specificDiscountValue) /
      10000
    ).toFixed(2);

    const discountedPrice = (originalPrice / 100 - discountRatio) * 100;

    return {
      discountedPrice,
      originalPrice
    };
  }

  static checkEnableBadge({ badgeInput, badgeElement, settings }) {
    const isChecked = badgeInput.checked;

    badgeElement.innerHTML = isChecked ? settings.specific_discount_text : '';

    badgeElement.style.display = isChecked ? 'inline-block' : 'none';

    badgeElement.style.opacity = 1;
  }
  /**
   *
   * @param quiz_with_tag
   * @param question_key
   * @returns {*|string}
   */
  static getAnswerRaw({ quiz_with_tag, question_key }) {
    const answer_raw = quiz_with_tag.multianswer
      ? quiz_with_tag.answers.map((obj) => obj.answer)
      : quiz_with_tag.answer;

    const isString = typeof answer_raw == 'string';

    if (isString && question_key == 'scalp_moisture') {
      let scalp_moistureDefaultValues = ['dry', 'normal', 'oily'];
      let validateScalp_moisture =
        scalp_moistureDefaultValues.includes(answer_raw);
      return validateScalp_moisture ? answer_raw : 'normal';
    }

    return answer_raw;
  }

  static GetRecommendedProductsFromUrl() {
    const urlParams = ConfigurationUtils.getUrlPrams();
    const recommendedProducts = urlParams?.get(`recommended_products`)
      ? urlParams?.get(`recommended_products`).split(',')
      : [];
    const additionalProducts = urlParams?.get(`explore_more_products`)
      ? urlParams?.get(`explore_more_products`).split(',')
      : [];

    const uniqueProducts = [
      ...new Set([...recommendedProducts, ...additionalProducts])
    ];

    return uniqueProducts.map((item) => ({
      shopify_product_id: '',
      shopify_variant_id: '',
      product_handle: item,
      product_options: {
        size: '16 oz',
        color: ''
      }
    }));
  }

  // should be temporal, but the logic is so tied that we need this mutation
  // for the original view configuration
  static MutateCustomConfigurationBasedOnProfileType(regimen, profileType) {
    switch (profileType) {
      case 'hair':
        regimen.raw_custom_configurations.skin_goals.enable = false;
        regimen.raw_custom_configurations.hair_goals.enable = true;
        regimen.raw_custom_configurations.style_goals.enable = true;
        break;
      case 'skin':
        regimen.raw_custom_configurations.skin_goals.enable = true;
        regimen.raw_custom_configurations.hair_goals.enable = false;
        regimen.raw_custom_configurations.style_goals.enable = false;
        break;
      case 'body':
        regimen.raw_custom_configurations.skin_goals.enable = false;
        regimen.raw_custom_configurations.hair_goals.enable = false;
        regimen.raw_custom_configurations.style_goals.enable = false;
        break;
      default:
        regimen.init_input_error_message.push(
          `Unkown profile type, please chooose the correct quiz for Hair, Body or Skin`
        );
    }
  }

  static async getQuizData() {
    const regimenType = ConfigurationUtils.getUrlPramByKey('profile_type');

    if (regimenType === 'hair') {
      const quizStore = localStorage.getItem('fobQuizData');
      if (!!quizStore) return JSON.parse(quizStore);
    }

    console.log('===== que pasa');

    const data = await VQBGateway.getFormattedQuizData();

    console.log('===== no pasa nada');

    return data;
  }
}
